import { buildCanonicalQuery, buildCatalogUrl, navigateTo, parseCanonicalFromUrl, t } from './wgf-utils.js';

export function hydrateFilterShortcode(root) {
  const form = root.querySelector('[data-wgf-filter-form="root"]');
  if (!form) return;
  const filterKey = root.dataset.wgfFilterKey || '';

  const updateMobileToggleCount = () => {
    const toggleId = root.dataset.wgfMobileToggleId || '';
    const toggleButton = toggleId ? document.getElementById(toggleId) : null;
    if (!toggleButton) return;
    const checkedCount = form.querySelectorAll('[data-wgf-filter-input="true"]:checked').length;
    const label = t('filterToggle', 'Filtrar');
    toggleButton.textContent = checkedCount > 0 ? `${label} (${checkedCount})` : label;
  };

  const closeMobileSheet = () => {
    root.classList.remove('is-mobile-open');
    document.querySelectorAll('[data-wgf-filters-backdrop="true"]').forEach((backdrop) => {
      backdrop.classList.remove('is-visible');
    });
    document.body.classList.remove('wgf-filters-mobile-lock');
  };

  if (root.dataset.wgfMobileSheetReady !== 'true') {
    root.dataset.wgfMobileSheetReady = 'true';
    const toggleButton = document.createElement('button');
    const safeKey = filterKey ? filterKey.replace(/[^a-z0-9_-]/gi, '') : 'default';
    const toggleId = `wgf-filters-toggle-${safeKey}`;
    toggleButton.id = toggleId;
    root.dataset.wgfMobileToggleId = toggleId;
    toggleButton.type = 'button';
    toggleButton.className = 'wgf-filters__mobile-toggle';
    toggleButton.setAttribute('data-wgf-filters-toggle', 'true');
    toggleButton.textContent = t('filterToggle', 'Filtrar');
    root.parentNode?.insertBefore(toggleButton, root);

    const backdrop = document.createElement('div');
    backdrop.className = 'wgf-filters__backdrop';
    backdrop.setAttribute('data-wgf-filters-backdrop', 'true');
    document.body.appendChild(backdrop);

    const setOpen = (open) => {
      root.classList.toggle('is-mobile-open', open);
      backdrop.classList.toggle('is-visible', open);
      document.body.classList.toggle('wgf-filters-mobile-lock', open);
      toggleButton.style.visibility = open ? 'hidden' : 'visible';
      toggleButton.style.pointerEvents = open ? 'none' : 'auto';
    };
    toggleButton.addEventListener('click', () => setOpen(true));
    backdrop.addEventListener('click', () => setOpen(false));
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') setOpen(false); });

    const clearButton = form.querySelector('[data-wgf-filter-clear="true"]');
    if (clearButton) clearButton.addEventListener('click', () => closeMobileSheet());
    form.addEventListener('change', updateMobileToggleCount);
    updateMobileToggleCount();
  }

  const config = (window.wgfFilterConfigs && filterKey && window.wgfFilterConfigs[filterKey]) ? window.wgfFilterConfigs[filterKey] : {};
  const currentCategoryUrl = typeof config.currentCategoryUrl === 'string' ? config.currentCategoryUrl.trim() : '';
  const currentCategorySlug = typeof config.currentCategory === 'string' ? config.currentCategory.trim() : '';

  const sync = () => {
    const canonical = parseCanonicalFromUrl(window.location.search);
    form.querySelectorAll('[data-wgf-filter-input="true"]').forEach((input) => {
      const key = input.getAttribute('name');
      if (!key) return;
      const values = (canonical[key] || '').split(',').map((item) => item.trim()).filter(Boolean);
      input.checked = values.includes(input.value);
    });
  };

  sync();
  updateMobileToggleCount();

  form.querySelectorAll('[data-wgf-category-link="true"]').forEach((link) => {
    const href = link.getAttribute('href') || '';
    if (!href || href === '#') return;
    const canonical = parseCanonicalFromUrl(window.location.search);
    delete canonical.filter_product_cat;
    const query = buildCanonicalQuery(canonical);
    link.setAttribute('href', `${href}${query}`);
  });

  const currentPath = window.location.pathname.replace(/\/+$/, '') || '/';
  form.querySelectorAll('[data-wgf-category-link="true"]').forEach((link) => {
    link.removeAttribute('aria-current');
    let linkPath = '';
    try {
      const parsed = new URL(link.href, window.location.origin);
      linkPath = parsed.pathname.replace(/\/+$/, '') || '/';
    } catch (error) {
      return;
    }
    if (linkPath === currentPath) link.setAttribute('aria-current', 'page');
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const canonical = parseCanonicalFromUrl(window.location.search);
    const grouped = {};
    form.querySelectorAll('[data-wgf-filter-input="true"]').forEach((input) => {
      const key = input.getAttribute('name');
      if (!key) return;
      if (!grouped[key]) grouped[key] = [];
      if (input.checked) {
        const value = (input.value || '').trim();
        if (value) grouped[key].push(value);
      }
    });
    Object.entries(grouped).forEach(([key, values]) => {
      if (values.length) {
        canonical[key] = values.join(',');
        const taxonomy = key.startsWith('filter_') ? key.substring('filter_'.length) : '';
        if (taxonomy) canonical[`query_type_${taxonomy}`] = 'or';
      }
    });

    Object.keys(canonical).forEach((key) => {
      if (!key.startsWith('filter_')) return;
      const hasChecked = form.querySelector(`[data-wgf-filter-input="true"][name="${key}"]:checked`);
      if (!hasChecked) delete canonical[key];
      const taxonomy = key.startsWith('filter_') ? key.substring('filter_'.length) : '';
      if (taxonomy) {
        const queryTypeKey = `query_type_${taxonomy}`;
        if (!hasChecked) delete canonical[queryTypeKey];
      }
    });
    if (!canonical.filter_product_cat && currentCategorySlug) canonical.filter_product_cat = currentCategorySlug;

    if (currentCategoryUrl) {
      closeMobileSheet();
      const params = new URLSearchParams();
      Object.entries(canonical).forEach(([key, value]) => {
        if (!key.startsWith('filter_') && !key.startsWith('query_type_')) return;
        if (key === 'filter_product_cat') return;
        const normalized = typeof value === 'string' ? value.trim() : '';
        if (!normalized) return;
        params.set(key, normalized);
      });
      const query = params.toString();
      navigateTo(query ? `${currentCategoryUrl}?${query}` : currentCategoryUrl);
      return;
    }
    closeMobileSheet();
    navigateTo(buildCatalogUrl(canonical, config));
  });

  const clearButton = form.querySelector('[data-wgf-filter-clear="true"]');
  if (clearButton) {
    clearButton.addEventListener('click', (event) => {
      event.preventDefault();
      form.querySelectorAll('[data-wgf-filter-input="true"]').forEach((input) => {
        input.checked = false;
      });
      if (currentCategoryUrl) {
        closeMobileSheet();
        navigateTo(currentCategoryUrl);
        return;
      }
      closeMobileSheet();
      navigateTo(buildCatalogUrl({}, config));
    });
  }
}
